import type { NodeData, Status } from './data';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface NodeLayout {
  id: string;
  label: string;
  description: string;
  level: number;       // 0 = main | 1 = sous-projet | 2 = tâche | 3+ = sous-tâche
  color: number;
  radius: number;      // rayon du cercle en pixels
  x: number;
  y: number;
  parentId: string | null;
  childIds: string[];
  angle: number;       // angle (rad) parent → ce nœud, pour placer ses enfants
  status: Status;
}

// ─── Configuration visuelle par niveau ───────────────────────────────────────

// Couleurs indexées par niveau — overridées par le thème au moment du rendu.
// Ces valeurs sont des fallbacks neutres, jamais utilisées en pratique.
const COLORS = [0x4caf50, 0x2196f3, 0xff9800, 0xe91e63];

// Rayons indexés par niveau — plus grands pour que le texte respire
const RADII = [70, 52, 38, 30];

// Distance centre-à-centre parent → enfants, indexée par niveau du parent
const DISTANCES = [220, 165, 135, 110];

// Écart angulaire (rad) entre enfants d'un même nœud (niveaux ≥ 1)
const SPREAD = 0.6;

// Écart entre racines quand plusieurs projets sont affichés côte-à-côte
const ROOT_SPACING_X = 1400;
const ROOT_SPACING_Y = 1200;

// ─── Algorithme de disposition circulaire (récursif, profondeur illimitée) ───

/**
 * Calcule les positions (x, y) de tous les nœuds.
 *
 * - La racine est centrée sur le canvas.
 * - Ses enfants directs (niveau 1) sont répartis à 360° autour d'elle.
 * - Chaque niveau suivant part en éventail dans la direction du parent.
 */
export function buildLayout(
  nodes: NodeData[],
  canvasWidth: number,
  canvasHeight: number,
): Map<string, NodeLayout> {
  const dataMap   = new Map(nodes.map(n => [n.id, n]));
  const layoutMap = new Map<string, NodeLayout>();

  // Racines = nœuds qui ne sont l'enfant d'aucun autre (dans l'ordre d'apparition)
  const allChildIds = new Set(nodes.flatMap(n => n.children));
  const roots       = nodes.filter(n => !allChildIds.has(n.id));
  if (roots.length === 0) return layoutMap;

  // Grille centrée autour du centre du canvas.
  // Pour un seul projet, cela revient exactement à le placer au centre.
  const cols    = Math.ceil(Math.sqrt(roots.length));
  const rows    = Math.ceil(roots.length / cols);
  const gridCx  = (cols - 1) / 2;
  const gridCy  = (rows - 1) / 2;
  const centerX = canvasWidth  / 2;
  const centerY = canvasHeight / 2;

  roots.forEach((rootData, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const rx  = centerX + (col - gridCx) * ROOT_SPACING_X;
    const ry  = centerY + (row - gridCy) * ROOT_SPACING_Y;

    layoutMap.set(rootData.id, {
      id:       rootData.id,
      label:    rootData.label,
      description: rootData.description ?? '',
      level:    0,
      color:    COLORS[0],
      radius:   RADII[0],
      x:        rx,
      y:        ry,
      parentId: null,
      childIds: rootData.children,
      angle:    0,
      status:   rootData.status ?? 'todo',
    });

    placeChildren(rootData.id, 0, 0, dataMap, layoutMap);
  });

  return layoutMap;
}

/**
 * Positionne récursivement les enfants d'un nœud déjà placé dans layoutMap.
 *
 * @param parentId    identifiant du nœud parent (déjà dans layoutMap)
 * @param parentAngle angle (rad) du grand-parent vers ce parent (pour l'éventail)
 * @param level       niveau du parent (ses enfants seront level+1)
 */
function placeChildren(
  parentId:    string,
  parentAngle: number,
  level:       number,
  dataMap:     Map<string, NodeData>,
  layoutMap:   Map<string, NodeLayout>,
): void {
  const parentLayout = layoutMap.get(parentId);
  const parentData   = dataMap.get(parentId);
  if (!parentLayout || !parentData || parentData.children.length === 0) return;

  const childLevel = level + 1;
  const color      = COLORS[Math.min(childLevel, COLORS.length - 1)];
  const radius     = RADII[Math.min(childLevel, RADII.length - 1)];
  const dist       = DISTANCES[Math.min(level, DISTANCES.length - 1)];
  const n          = parentData.children.length;

  parentData.children.forEach((childId, i) => {
    const childData = dataMap.get(childId);
    if (!childData) return;

    let angle: number;

    if (level === 0) {
      // Enfants directs de la racine : répartis uniformément sur 360°
      angle = (i / n) * Math.PI * 2;
    } else {
      // Éventail centré sur la direction parent→grandParent
      const offset = (i - (n - 1) / 2) * SPREAD;
      angle = parentAngle + offset;
    }

    const x = parentLayout.x + Math.cos(angle) * dist;
    const y = parentLayout.y + Math.sin(angle) * dist;

    layoutMap.set(childId, {
      id:       childId,
      label:    childData.label,
      description: childData.description ?? '',
      level:    childLevel,
      color,
      radius,
      x,
      y,
      parentId: parentId,
      childIds: childData.children,
      angle,
      status:   childData.status ?? 'todo',
    });

    // Descendre récursivement
    placeChildren(childId, angle, childLevel, dataMap, layoutMap);
  });
}

// ─── Re-layout d'un sous-arbre autour de la position actuelle d'un nœud ──────

/**
 * Recalcule (x, y, angle) de tous les descendants de `id` en utilisant la
 * position actuelle du nœud. L'angle "sortant" du nœud est dérivé de la
 * direction réelle parent → nœud, ce qui permet de drag-puis-rotate naturellement.
 */
export function relayoutDescendants(
  id:        string,
  layoutMap: Map<string, NodeLayout>,
): void {
  const node = layoutMap.get(id);
  if (!node || node.childIds.length === 0) return;

  // Recompute the "outward" angle from the actual parent → node direction.
  // For a root (no parent), leave angle at 0 — children fan out 360°.
  if (node.parentId) {
    const parent = layoutMap.get(node.parentId);
    if (parent) {
      node.angle = Math.atan2(node.y - parent.y, node.x - parent.x);
    }
  }

  relayoutRecursive(node, layoutMap);
}

// ─── Construire une NodeLayout pour un nœud créé à l'exécution ───────────────

export interface NewLayoutOpts {
  id:          string;
  label:       string;
  description?: string;
  level:       number;
  status:      Status;
  parentId:    string | null;
  childIds:    string[];
  x:           number;
  y:           number;
}

export function makeNodeLayout(opts: NewLayoutOpts): NodeLayout {
  const lvl = opts.level;
  return {
    id:          opts.id,
    label:       opts.label,
    description: opts.description ?? '',
    level:    lvl,
    color:    COLORS[Math.min(lvl, COLORS.length - 1)] as number,
    radius:   RADII[Math.min(lvl, RADII.length - 1)]  as number,
    x:        opts.x,
    y:        opts.y,
    parentId: opts.parentId,
    childIds: opts.childIds,
    angle:    0,
    status:   opts.status,
  };
}

function relayoutRecursive(parent: NodeLayout, layoutMap: Map<string, NodeLayout>): void {
  if (parent.childIds.length === 0) return;

  const level = parent.level;
  const dist  = DISTANCES[Math.min(level, DISTANCES.length - 1)];
  const n     = parent.childIds.length;

  parent.childIds.forEach((childId, i) => {
    const child = layoutMap.get(childId);
    if (!child) return;

    const angle = level === 0
      ? (i / n) * Math.PI * 2
      : parent.angle + (i - (n - 1) / 2) * SPREAD;

    child.x     = parent.x + Math.cos(angle) * dist;
    child.y     = parent.y + Math.sin(angle) * dist;
    child.angle = angle;

    relayoutRecursive(child, layoutMap);
  });
}
